(() => {
  if (customElements.get('storefront-coupon-offer')) return;
  const config = document.getElementById('storefront-coupon-context');
  if (!config) return;
  const cookieName = 'ryziger_offer_code';
  const root = window.Shopify?.routes?.root || '/';
  const params = new URLSearchParams(window.location.search);
  const elements = new Set();
  const requests = new Map();
  const queue = [];
  const permittedSubmissions = new WeakSet();
  let running = 0;
  let codes = [];
  let cartReady = Promise.resolve();
  let cartIsReady = false;

  function fingerprint(value) {
    let hash = 2166136261;
    for (const character of value.toUpperCase()) hash = Math.imul(hash ^ character.charCodeAt(0), 16777619);
    return (hash >>> 0).toString(36);
  }

  function mayRecover(value) {
    try {
      const previous = JSON.parse(localStorage.getItem('ryziger_offer_expiry'));
      return !previous || previous.fingerprint !== fingerprint(value) || previous.expires > Date.now();
    } catch { return true; }
  }

  function normalize(value) {
    return [...new Set(String(value || '').split(',').map(code => code.trim()).filter(code => code && code.length <= 255 && !/[\x00-\x1f\x7f]/.test(code)))].slice(0, 5);
  }

  function remember(value) {
    codes = normalize(value);
    try {
      const previous = storedCoupon();
      const expires = previous?.codes === codes.join(',') ? previous.expires : Date.now() + 86400000;
      const record = JSON.stringify({codes: codes.join(','), expires});
      document.cookie = `${cookieName}=${encodeURIComponent(record)}; Path=/; Max-Age=${codes.length ? Math.max(0, Math.floor((expires - Date.now()) / 1000)) : 0}; SameSite=Lax; Secure`;
      if (codes.length) localStorage.setItem('ryziger_offer_expiry', JSON.stringify({fingerprint: fingerprint(codes.join(',')), expires}));
    } catch {}
  }

  function storedCoupon() {
    try {
      const value = document.cookie.split('; ').find(row => row.startsWith(`${cookieName}=`));
      if (!value) return null;
      const record = JSON.parse(decodeURIComponent(value.slice(cookieName.length + 1)));
      return record.expires > Date.now() ? record : null;
    } catch { return null; }
  }

  function remembered() {
    return storedCoupon()?.codes || '';
  }

  function redirectedCoupon() {
    try {
      const cookie = document.cookie.split('; ').find(row => row.startsWith('discount_code='));
      const value = cookie ? decodeURIComponent(cookie.slice('discount_code='.length)) : '';
      const key = fingerprint(value);
      const previous = localStorage.getItem('ryziger_native_coupon');
      localStorage.setItem('ryziger_native_coupon', key);
      return value && previous !== key && mayRecover(value) ? value : '';
    } catch { return ''; }
  }

  function money(amount, currency) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 2
    }).format(amount);
  }

  function refresh() {
    for (const element of elements) element.update();
    decorateLinks(document);
  }

  function decorateLinks(scope) {
    for (const link of scope.querySelectorAll('a[href]')) {
      const url = new URL(link.href, location.href);
      if (!codes.length) {
        if (link.hasAttribute('data-remembered-coupon')) {
          url.searchParams.delete('discount');
          link.href = url.href;
          link.removeAttribute('data-remembered-coupon');
        }
        continue;
      }
      if (url.origin !== location.origin || !/\/(products|collections)\//.test(url.pathname)) continue;
      if (link.closest('[data-article-promo-code]') || url.searchParams.has('discount') && !link.hasAttribute('data-remembered-coupon')) continue;
      url.searchParams.set('discount', codes.join(','));
      link.href = url.href;
      link.setAttribute('data-remembered-coupon', '');
    }
  }

  function enqueue(work) {
    return new Promise((resolve, reject) => {
      queue.push({work, resolve, reject});
      drain();
    });
  }

  function drain() {
    while (running < 2 && queue.length) {
      const task = queue.shift();
      running++;
      task.work().then(task.resolve, task.reject).finally(() => { running--; drain(); });
    }
  }

  const quoteQuery = `mutation PreviewProductCoupon($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        discountCodes { code applicable }
        lines(first: 1) { nodes {
          quantity
          cost { amountPerQuantity { amount currencyCode } subtotalAmount { amount currencyCode } }
          discountAllocations(lineLevelOnly: false) {
            __typename targetType discountedAmount { amount currencyCode }
            ... on CartCodeDiscountAllocation {
              code
              sourceDiscountApplication { value {
                __typename
                ... on MoneyV2 { amount currencyCode }
                ... on PricingPercentageValue { percentage }
              } }
            }
          }
        } }
      }
      userErrors { field message code }
    }
  }`;

  function calculate(cart, requestedCodes, quantity, expectedPrice, currency) {
    const line = cart?.lines?.nodes?.[0];
    if (!line || line.quantity !== quantity || line.cost.subtotalAmount.currencyCode !== currency) return null;
    if (Math.abs(Number(line.cost.amountPerQuantity.amount) * 100 - expectedPrice) > 1) return null;
    const wanted = new Set(requestedCodes.map(code => code.toUpperCase()));
    const allocations = line.discountAllocations.filter(item => item.targetType === 'LINE_ITEM');
    const matched = allocations.filter(item => item.__typename === 'CartCodeDiscountAllocation' && wanted.has(item.code.toUpperCase()));
    if (!matched.length || matched.some(item => !cart.discountCodes.some(code => code.applicable && code.code.toUpperCase() === item.code.toUpperCase()))) return null;
    const couponSavings = matched.reduce((sum, item) => sum + Math.round(Number(item.discountedAmount.amount) * 100), 0);
    const allSavings = allocations.reduce((sum, item) => sum + Math.round(Number(item.discountedAmount.amount) * 100), 0);
    const subtotal = Math.round(Number(line.cost.subtotalAmount.amount) * 100);
    if (couponSavings <= 0 || allSavings > subtotal) return null;
    const value = matched.length === 1 ? matched[0].sourceDiscountApplication.value : null;
    const savingsLabel = value?.__typename === 'PricingPercentageValue'
      ? `${new Intl.NumberFormat(document.documentElement.lang || 'en', {maximumFractionDigits: 2}).format(value.percentage)}%`
      : money(couponSavings / 100, currency);
    return {savingsLabel, priceLabel: money((subtotal - allSavings) / 100, currency)};
  }

  function quote(variantId, quantity, country, currency, expectedPrice, sellingPlan) {
    const requestedCodes = [...codes];
    const key = JSON.stringify([variantId, quantity, country, currency, expectedPrice, sellingPlan, requestedCodes]);
    const cacheKey = `ryziger-quote:${key}`;
    try {
      const cached = JSON.parse(sessionStorage.getItem(cacheKey));
      if (cached && Date.now() - cached.time < 60000) return Promise.resolve(cached.value);
    } catch {}
    if (requests.has(key)) return requests.get(key);
    const promise = enqueue(async () => {
      const line = {merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity};
      if (sellingPlan) line.sellingPlanId = `gid://shopify/SellingPlan/${sellingPlan}`;
      const response = await fetch('/api/2026-10/graphql.json', {
        method: 'POST', credentials: 'omit',
        headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
        body: JSON.stringify({query: quoteQuery, variables: {input: {lines: [line], discountCodes: requestedCodes, buyerIdentity: {countryCode: country}}}}),
        signal: AbortSignal.timeout(10000)
      });
      if (!response.ok) throw new Error('Coupon quote unavailable');
      const body = await response.json();
      if (body.errors?.length || body.data?.cartCreate?.userErrors?.length) throw new Error('Coupon quote unavailable');
      const value = calculate(body.data?.cartCreate?.cart, requestedCodes, quantity, expectedPrice, currency);
      try { sessionStorage.setItem(cacheKey, JSON.stringify({time: Date.now(), value})); } catch {}
      return value;
    });
    requests.set(key, promise);
    promise.finally(() => requests.delete(key)).catch(() => {});
    return promise;
  }

  class StorefrontCouponOffer extends HTMLElement {
    connectedCallback() {
      elements.add(this);
      this.revision = 0;
      this.abort = new AbortController();
      const form = document.getElementById(this.dataset.formId);
      if (form) {
        document.addEventListener('change', event => {
          if (event.target.form === form && ['quantity', 'id', 'selling_plan'].includes(event.target.name)) this.update();
        }, {signal: this.abort.signal});
        form.addEventListener('variant:change', event => {
          if (!event.detail.variant?.available) { this.hidden = true; return; }
          this.dataset.variantId = event.detail.variant.id;
          this.dataset.price = event.detail.variant.price;
          this.update();
        }, {signal: this.abort.signal});
        this.visibleForQuote = true;
        this.update();
      } else {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) {
            this.visibleForQuote = true;
            this.observer.disconnect();
            this.update();
          }
        }, {rootMargin: '150px'});
        this.observer.observe(this.closest('product-card') || this.parentElement);
      }
    }

    disconnectedCallback() {
      elements.delete(this);
      this.abort?.abort();
      this.observer?.disconnect();
      this.revision++;
    }

    async update() {
      const revision = ++this.revision;
      this.hidden = true;
      if (!codes.length || !this.visibleForQuote) return;
      const form = document.getElementById(this.dataset.formId);
      const quantity = Math.max(1, Number(form?.elements.namedItem('quantity')?.value || 1));
      const variantId = form?.elements.namedItem('id')?.value || this.dataset.variantId;
      const sellingPlan = form?.elements.namedItem('selling_plan')?.value || '';
      if (!Number.isInteger(quantity) || quantity > 250 || !/^\d+$/.test(variantId)) return;
      try {
        const result = await quote(variantId, quantity, this.dataset.country, this.dataset.currency, Number(this.dataset.price), sellingPlan);
        if (revision !== this.revision || !this.isConnected || !result) return;
        this.querySelector('.storefront-coupon-offer__badge').textContent = this.dataset.extraLabel.replace('__SAVINGS__', result.savingsLabel);
        this.querySelector('.storefront-coupon-offer__price strong').textContent = result.priceLabel;
        this.querySelector('.storefront-coupon-offer__price span').textContent = this.dataset.afterLabel + (quantity > 1 ? ` ${this.dataset.quantityLabel.replace('__QUANTITY__', quantity)}` : '');
        this.hidden = false;
      } catch { if (revision === this.revision) this.hidden = true; }
    }
  }

  customElements.define('storefront-coupon-offer', StorefrontCouponOffer);

  async function initialize() {
    const explicit = params.has('discount');
    const redirected = redirectedCoupon();
    remember(explicit ? params.get('discount') : redirected || remembered());
    if (!explicit && !codes.length) return;
    try {
      const response = await fetch(`${root}cart.js`, {headers: {'Accept': 'application/json'}});
      if (!response.ok) return;
      const cart = await response.json();
      const current = normalize((cart.discount_codes || []).map(code => code.code).join(','));
      if (!explicit && config.dataset.page === 'cart') remember(current.join(','));
      refresh();
      if (codes.join(',').toUpperCase() === current.join(',').toUpperCase()) return;
      if (!explicit && !codes.length) return;
      const update = await fetch(`${root}cart/update.js`, {
        method: 'POST', headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
        body: JSON.stringify({discount: codes.join(',')})
      });
      if (!update.ok) throw new Error('Coupon application unavailable');
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {bubbles: true}));
    } catch {
      for (const element of elements) element.hidden = true;
    }
  }

  cartReady = initialize().finally(() => { cartIsReady = true; });

  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const removal = event.target.closest('cart-discount-remove-button');
    if (removal) {
      remember(codes.filter(code => code.toUpperCase() !== removal.getAttribute('discount-code')?.toUpperCase()).join(','));
      refresh();
      return;
    }
    const link = event.target.closest('a[href]');
    if (link) decorateLinks(link.parentElement);
  }, true);

  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !new URL(form.action).pathname.endsWith('/cart/add') || form.hasAttribute('data-article-promo-code')) return;
    if (!codes.length || cartIsReady || permittedSubmissions.has(form)) { permittedSubmissions.delete(form); return; }
    event.preventDefault();
    event.stopImmediatePropagation();
    const submitter = event.submitter;
    cartReady.finally(() => setTimeout(() => {
      permittedSubmissions.add(form);
      form.requestSubmit(submitter?.isConnected ? submitter : undefined);
    }, 0));
  }, true);

  document.addEventListener('shopify:section:load', () => decorateLinks(document));
})();
